import { UniqueIdentifier } from "@dnd-kit/core"
import { DayViewEvent, ScheduledEvent } from "../../scheduleApi"
import dayjs from "dayjs"

export type ReorderEventLocation = {
  id: string
  jobName: string
  latitude: number
  longitude: number
  address1: string
  address2: string | null
  city: string
  stateCode: string
  zip: string
}

export type ReorderDayViewState = {
  events: ScheduledEvent[]
  startTime: string
  startingLocation: ReorderEventLocation | undefined
  endingLocation: ReorderEventLocation | undefined
  hasChanges: boolean
}

export const defaultReorderDayViewState: ReorderDayViewState = {
  events: [],
  startTime: dayjs().hour(8).minute(0).second(0).toISOString(),
  startingLocation: undefined,
  endingLocation: undefined,
  hasChanges: false
}

export type Action =
  | { type: 'EVENTS_LOADED', value: ScheduledEvent[], setHasChanges?: boolean }
  | { type: 'START_TIME_CHANGED', value: string, isInitialLoad?: boolean }
  | { type: 'STARTING_LOCATION_CHANGED', value: ReorderEventLocation | undefined, isInitialLoad?: boolean }
  | { type: 'ENDING_LOCATION_CHANGED', value: ReorderEventLocation | undefined, isInitialLoad?: boolean }
  | { type: 'SHOP_COORDINATES_UPDATED', latitude: number, longitude: number }
  | { type: 'EVENT_LOCATION_UPDATED', eventId: number, latitude: number, longitude: number }

export const reducer = (state: ReorderDayViewState, action: Action): ReorderDayViewState => {
  switch (action.type) {
    case 'EVENTS_LOADED':
      return {
        ...state,
        events: action.value,
        hasChanges: action.setHasChanges ? true : state.hasChanges
      }
    case 'START_TIME_CHANGED':
      return {
        ...state,
        startTime: action.value,
        hasChanges: action.isInitialLoad ? state.hasChanges : true
      }
    case 'STARTING_LOCATION_CHANGED':
      return {
        ...state,
        startingLocation: action.value,
        hasChanges: action.isInitialLoad ? state.hasChanges : true
      }
    case 'ENDING_LOCATION_CHANGED':
      return {
        ...state,
        endingLocation: action.value,
        hasChanges: action.isInitialLoad ? state.hasChanges : true
      }
    case 'SHOP_COORDINATES_UPDATED': {
      const newState = { ...state }
      if (state.startingLocation?.id?.startsWith('shop-')) {
        newState.startingLocation = { ...state.startingLocation, latitude: action.latitude, longitude: action.longitude }
      }
      if (state.endingLocation?.id?.startsWith('shop-')) {
        newState.endingLocation = { ...state.endingLocation, latitude: action.latitude, longitude: action.longitude }
      }
      return newState
    }
    case 'EVENT_LOCATION_UPDATED':
      return {
        ...state,
        events: state.events.map(event =>
          event.id === action.eventId
            ? { ...event, latitude: action.latitude, longitude: action.longitude }
            : event
        )
      }
    default:
      return state
  }
}

export const dragReorderOrderChanged = (active: UniqueIdentifier, over: UniqueIdentifier, state: ReorderDayViewState): ScheduledEvent[] => {
  const oldIndex = state.events.findIndex((event) => event.id === active)
  const newIndex = state.events.findIndex((event) => event.id === over)

  const newEvents = [...state.events]
  const [removed] = newEvents.splice(oldIndex, 1)
  newEvents.splice(newIndex, 0, removed)

  return newEvents
}

export const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const remainingMinutes = Math.floor((seconds % 3600) / 60)
  const parts: string[] = []
  if (hours > 0) parts.push(`${hours}h`)
  if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`)
  return parts.length > 0 ? parts.join(' ') : '0m'
}

export const formatDistance = (meters: number): string => {
  const miles = (meters / 1609.34).toFixed(1)
  return miles
}

export const createEventLocation = (event: DayViewEvent): ReorderEventLocation => ({
  id: `event-${event.id}`,
  jobName: event.jobName || 'Unnamed Job',
  latitude: event.latitude || 0,
  longitude: event.longitude || 0,
  address1: event.customerAddress1 || '',
  address2: event.customerAddress2 || null,
  city: event.customerCity || '',
  stateCode: event.customerState || '',
  zip: event.customerZip || ''
})

export const sliceEventsForOptimization = (
  events: ScheduledEvent[],
  range: [number, number]
): { eventsBefore: ScheduledEvent[]; eventsInRange: ScheduledEvent[]; eventsAfter: ScheduledEvent[] } => {
  const startIndex = range[0] - 1
  const endIndex = range[1] - 1
  return {
    eventsBefore: events.slice(0, startIndex),
    eventsInRange: events.slice(startIndex, endIndex + 1),
    eventsAfter: events.slice(endIndex + 1),
  }
}

export const recombineOptimizedEvents = (
  eventsBefore: ScheduledEvent[],
  optimizedEventsInRange: ScheduledEvent[],
  eventsAfter: ScheduledEvent[]
): ScheduledEvent[] => {
  return [...eventsBefore, ...optimizedEventsInRange, ...eventsAfter]
}

export const isMultiDayEvent = (event: DayViewEvent): boolean => {
  const start = dayjs(event.start)
  const end = dayjs(event.end)
  const durationHours = end.diff(start, 'hour')
  return durationHours >= 24
}
